import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { BonificoPanel, MetodoSwitch, SumUpPanel } from "@/components/BonificoPanel";
import { PawPrint, Heart, CheckCircle2, ArrowLeft } from "lucide-react";

export default function DonazionePubblica() {
  const [amount, setAmount] = useState(25);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [done, setDone] = useState(false);
  const [cf, setCf] = useState("");
  const [metodo, setMetodo] = useState("bonifico");

  const { data: config } = useQuery({ queryKey: ["paypal-config"], queryFn: async () => (await api.get("/payments/config")).data });
  const presets = config?.donazione_presets ?? [10, 25, 50, 100];
  const valid = amount >= 1 && amount <= 10000;
  const validBonifico = valid && name.trim() && /\S+@\S+\.\S+/.test(email);
  const causaleDono = `${config?.causale_donazione || "Erogazione liberale"}${name.trim() ? ` - ${name.trim()}` : ""}`;

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-[#14382C] px-6 py-5">
        <div className="mx-auto flex max-w-3xl items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#166534]"><PawPrint className="h-5 w-5 text-white" /></div>
          <span className="font-heading text-lg font-extrabold text-white">Guardie Zoofile</span>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-6 py-10 gz-fade-up">
        <Link to="/login" className="mb-6 inline-flex items-center gap-2 text-sm text-[#166534] hover:underline">
          <ArrowLeft className="h-4 w-4" /> Torna all'accesso
        </Link>

        {done ? (
          <Card className="border-slate-200 shadow-sm">
            <CardContent className="py-16 text-center">
              <CheckCircle2 className="mx-auto h-14 w-14 text-green-600" />
              <h1 className="mt-4 font-heading text-2xl font-extrabold text-slate-900">Grazie di cuore!</h1>
              <p className="mt-2 text-slate-500">{done === "bonifico" ? "Abbiamo ricevuto la segnalazione del tuo pagamento: alla verifica dell'accredito ti invieremo conferma e ricevuta." : "La tua donazione sostiene il lavoro delle guardie zoofile sul territorio."}</p>
            </CardContent>
          </Card>
        ) : (
          <>
            <div className="mb-8">
              <div className="mb-3 inline-flex h-12 w-12 items-center justify-center rounded-xl bg-rose-50"><Heart className="h-6 w-6 text-rose-500" /></div>
              <h1 className="font-heading text-3xl font-extrabold tracking-tight text-slate-900">Sostieni le guardie zoofile</h1>
              <p className="mt-2 text-slate-500">La tua donazione aiuta la tutela e il soccorso degli animali. Ogni contributo conta.</p>
            </div>

            <Card className="border-slate-200 shadow-sm">
              <CardContent className="space-y-5 p-6">
                <div>
                  <Label className="mb-2 block">Scegli un importo</Label>
                  <div className="flex flex-wrap gap-2">
                    {presets.map((p) => (
                      <button key={p} data-testid={`dona-preset-${p}`} onClick={() => setAmount(p)}
                        className={`rounded-full border px-5 py-2 text-sm font-semibold transition-colors ${
                          amount === p ? "border-[#14382C] bg-[#14382C] text-white" : "border-slate-300 text-slate-600 hover:border-[#14382C]"
                        }`}>€ {p}</button>
                    ))}
                  </div>
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label>Importo (EUR)</Label>
                    <Input type="number" min={1} max={10000} step="0.01" data-testid="dona-amount" value={amount} onChange={(e) => setAmount(parseFloat(e.target.value) || 0)} />
                  </div>
                  <div className="space-y-2">
                    <Label>Nome e cognome *</Label>
                    <Input data-testid="dona-name" value={name} onChange={(e) => setName(e.target.value)} />
                  </div>
                  <div className="space-y-2">
                    <Label>Email *</Label>
                    <Input type="email" data-testid="dona-email" value={email} onChange={(e) => setEmail(e.target.value)} />
                  </div>
                  <div className="space-y-2">
                    <Label>Codice Fiscale (per la detrazione)</Label>
                    <Input data-testid="dona-cf" value={cf} maxLength={16} onChange={(e) => setCf(e.target.value.toUpperCase())} />
                  </div>
                </div>
                <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-xs text-emerald-900" data-testid="dona-causale-info">
                  La donazione è un'<strong>erogazione liberale</strong> a favore di un'Organizzazione di Volontariato (art. 83 D.Lgs. 117/2017). Per le agevolazioni fiscali conserva la ricevuta del pagamento e indica il tuo Codice Fiscale: ti invieremo la ricevuta dell'Associazione.
                </div>
                <MetodoSwitch value={metodo} onChange={setMetodo} />
                {metodo === "bonifico" ? (
                  <BonificoPanel config={config} tipo="donazione" amount={amount} causaleBonifico={causaleDono}
                    donorName={name} donorEmail={email} donorCf={cf} disabled={!validBonifico} onSuccess={() => setDone("bonifico")} />
                ) : (
                <SumUpPanel config={config} tipo="donazione" amount={amount} causaleBonifico={causaleDono}
                  donorName={name} donorEmail={email} donorCf={cf} disabled={!validBonifico} onSuccess={() => setDone("bonifico")} />
                )}
              </CardContent>
            </Card>
          </>
        )}
      </main>
    </div>
  );
}
